/*
 * ACTIVACIÓN DIFERIDA DE LA VERSIÓN NUEVA (RF-085, D-81, RN-130b, RN-131).
 *
 * REGLA RECTORA (D-80): ninguna optimización de caché, actualización o reconexión
 * puede costar datos escritos y no guardados. Por eso una versión nueva detectada
 * por el service worker NUNCA se activa por debajo de una captura en curso: queda
 * EN ESPERA y se avisa de forma no bloqueante.
 *
 * CUÁNDO SE APLICA (RF-085, RN-131):
 *   * al cerrarse o guardarse el formulario que estaba sucio — es decir, cuando se
 *     libera la última captura en curso;
 *   * cuando la persona la acepta explícitamente ("Actualizar ahora");
 *   * y, si nunca hubo captura, en la siguiente carga natural de la aplicación,
 *     que es el comportamiento propio del service worker.
 *
 * CASO BORDE RN-131: varias detecciones seguidas conservan UNA sola espera y UN
 * solo aviso; no se apilan cintas.
 *
 * PROBABLE SIN SERVICE WORKER REAL: el gestor es una máquina de estados en memoria
 * con `recargar` y `contenedor` inyectables. Las pruebas anuncian la versión nueva
 * a mano y comprueban qué se aplica y qué no.
 */

const capturasEnCurso = new Set();
const observadores = new Set();

let versionEnEspera = false;
let trabajadorEnEspera = null;
let recargar = () => { if (typeof location !== 'undefined') location.reload(); };
let yaSeRecargo = false;

/** Estado observable por la cinta de aviso. */
export function estadoDeActualizacion() {
  return {
    hayVersionEnEspera: versionEnEspera,
    hayCapturaEnCurso: capturasEnCurso.size > 0,
  };
}

/** Suscribe un observador; se le notifica de inmediato con el estado actual. */
export function suscribirAActualizacion(observador) {
  observadores.add(observador);
  observador(estadoDeActualizacion());
  return () => observadores.delete(observador);
}

/**
 * RN-128: mientras haya un formulario sucio registrado, hay captura en curso. El
 * identificador es el del formulario (su clave de borrador), de modo que dos
 * formularios sucios cuenten como dos y liberar uno no libere al otro.
 */
export function marcarCapturaEnCurso(identificador) {
  if (!identificador) return;
  const habia = capturasEnCurso.size;
  capturasEnCurso.add(identificador);
  if (habia !== capturasEnCurso.size) notificar();
}

/**
 * Se libera al guardar, al cancelar, al dejar de estar sucio o al desmontarse el
 * formulario. Si era la última captura en curso y había una versión esperando, ese
 * es el momento seguro: se aplica (RN-131).
 */
export function liberarCaptura(identificador) {
  if (!capturasEnCurso.delete(identificador)) return;
  notificar();
  if (capturasEnCurso.size === 0 && versionEnEspera) aplicarVersionPendiente();
}

/** RN-130b: con formulario sucio está prohibido aplicar una versión nueva. */
export function hayCapturaEnCurso() {
  return capturasEnCurso.size > 0;
}

/**
 * Detección de versión nueva. Con captura en curso queda en espera y se avisa una
 * sola vez; sin captura en curso se conserva la espera y la cinta la ofrece, sin
 * recargar por sorpresa a quien está leyendo una pantalla.
 */
export function anunciarVersionNueva(trabajador = null) {
  trabajadorEnEspera = trabajador ?? trabajadorEnEspera;
  if (versionEnEspera) return false; // RN-131: una sola espera, un solo aviso.
  versionEnEspera = true;
  notificar();
  return true;
}

/** "Después": la versión sigue en espera y no se recarga nada (RN-130b). */
export function posponerVersion() {
  return estadoDeActualizacion();
}

/**
 * Aplica la versión en espera. Con captura en curso NO procede salvo que la
 * persona lo acepte explícitamente (`forzar`), y aun entonces lo capturado
 * sobrevive porque el borrador ya está escrito en el dispositivo (RN-144e).
 */
export function aplicarVersionPendiente({ forzar = false } = {}) {
  if (!versionEnEspera) return false;
  if (capturasEnCurso.size > 0 && !forzar) return false;

  versionEnEspera = false;
  notificar();

  try {
    trabajadorEnEspera?.postMessage('ACTUALIZAR_AHORA');
  } catch {
    // Sin trabajador en espera igual se recarga: la carga siguiente toma la
    // versión nueva por el camino normal del service worker.
  }
  recargarUnaVez();
  return true;
}

/**
 * Registro del service worker con detección de versión nueva (RF-017, RF-085).
 * Se llama después del primer pintado para no competir con el arranque (RNF-007).
 */
export function registrarServiceWorker({
  contenedor = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
  ruta = '/sw.js',
} = {}) {
  if (!contenedor) return Promise.resolve(null);

  return contenedor.register(ruta).then((registro) => {
    // Ya había una versión esperando de una visita anterior.
    if (registro.waiting) anunciarVersionNueva(registro.waiting);

    registro.addEventListener?.('updatefound', () => {
      const entrante = registro.installing;
      if (!entrante) return;
      entrante.addEventListener?.('statechange', () => {
        // Sólo cuenta como versión nueva si ya hay una controlando la página:
        // la primera instalación no es una actualización.
        if (entrante.state === 'installed' && contenedor.controller) {
          anunciarVersionNueva(registro.waiting ?? entrante);
        }
      });
    });

    return registro;
  }).catch(() => null);
}

/** Permite a las pruebas sustituir la recarga y evitar tocar `location`. */
export function configurarGestorDeActualizacion({ recargar: nuevaRecarga } = {}) {
  if (typeof nuevaRecarga === 'function') recargar = nuevaRecarga;
}

/** Deja el gestor como recién arrancado. Sólo para pruebas. */
export function reiniciarGestorDeActualizacion() {
  capturasEnCurso.clear();
  observadores.clear();
  versionEnEspera = false;
  trabajadorEnEspera = null;
  yaSeRecargo = false;
}

function recargarUnaVez() {
  if (yaSeRecargo) return;
  yaSeRecargo = true;
  recargar();
}

function notificar() {
  const estado = estadoDeActualizacion();
  for (const observador of observadores) observador(estado);
}
